import React from "react";
import styled from "styled-components";
import { Button, Dropdown, Tooltip } from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  DownOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const LAYOUT_BREAK = `(min-width: 768px)`;

const ContactStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const ContactWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  min-width: 0;

  @media ${LAYOUT_BREAK} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    width: auto;
    flex: 0 1 auto;
    max-width: 100%;
    gap: 10px;
  }
`;

const ContactButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  width: 100%;
  font-weight: 600;
  border-radius: 10px;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);

  @media ${LAYOUT_BREAK} {
    width: auto;
    min-width: 132px;
    max-width: min(240px, 100%);
  }

  &.ant-btn-primary {
    background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
    border: none;
    color: #fff;
    box-shadow: 0 2px 8px rgba(245, 87, 34, 0.28);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #f4511e 0%, #e64a19 100%);
      color: #fff;
      box-shadow: 0 4px 12px rgba(245, 87, 34, 0.38);
    }

    &:disabled {
      opacity: 0.55;
    }
  }
`;

const MarkSecondaryBtn = styled(Button)`
  height: 40px;
  width: 100%;
  font-weight: 500;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  color: #475569;
  background: #ffffff;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);

  @media ${LAYOUT_BREAK} {
    width: auto;
    flex: 0 0 auto;
    padding: 0 14px;
    max-width: 220px;
  }

  &:hover:not(:disabled) {
    border-color: rgba(255, 87, 34, 0.45) !important;
    color: #ea580c !important;
    background: #fffaf8 !important;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #9a3412;
  font-weight: 500;
  font-size: 0.8125rem;
  line-height: 1.5;
  text-align: left;
  width: 100%;
  min-width: 0;

  .anticon {
    margin-top: 2px;
    color: #ea580c;
    flex-shrink: 0;
  }
`;

function withNameTooltip(node, vendorName, showTip) {
  if (!showTip || !vendorName) return node;
  return (
    <Tooltip title={vendorName} placement="top">
      {node}
    </Tooltip>
  );
}

/**
 * ContactVendor - direct vendor contact (phone / email / dropdown).
 * Use compactLabels + inlineFooter inside narrow modals so labels stay short.
 */
function ContactVendor({
  vendorName = "Vendor",
  vendorPhone = null,
  vendorEmail = null,
  onMarkCompleted = null,
  isCompleted = false,
  compactLabels = false,
  inlineFooter = false,
}) {
  if (isCompleted) {
    return (
      <SuccessMessage>
        <CheckCircleOutlined />
        <span>
          Vendor contacted. You and the vendor were sent a summary by email and
          text. Reach out directly to schedule the work.
        </span>
      </SuccessMessage>
    );
  }

  const callLabel = compactLabels ? "Call vendor" : `Call ${vendorName}`;
  const emailLabel = compactLabels ? "Email vendor" : `Email ${vendorName}`;
  const contactMenuLabel = compactLabels
    ? "Contact vendor"
    : `Contact ${vendorName}`;

  const contactOptions = [];

  if (vendorPhone) {
    contactOptions.push({
      key: "phone",
      label: (
        <a
          href={`tel:${vendorPhone}`}
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <PhoneOutlined />
          {callLabel}
        </a>
      ),
    });
  }

  if (vendorEmail) {
    contactOptions.push({
      key: "email",
      label: (
        <a
          href={`mailto:${vendorEmail}?subject=Project Discussion - Clumse Referral`}
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <MailOutlined />
          {emailLabel}
        </a>
      ),
    });
  }

  const inner = (() => {
    if (contactOptions.length === 0) {
      return null;
    }

    if (contactOptions.length === 1) {
      const option = contactOptions[0];
      if (option.key === "phone") {
        const btn = (
          <ContactButton
            type="primary"
            icon={<PhoneOutlined />}
            onClick={() => {
              window.location.href = `tel:${vendorPhone}`;
            }}
          >
            {callLabel}
          </ContactButton>
        );
        return (
          <>
            {withNameTooltip(btn, vendorName, compactLabels)}
            {onMarkCompleted ? (
              <MarkSecondaryBtn type="default" onClick={onMarkCompleted}>
                Mark project complete
              </MarkSecondaryBtn>
            ) : null}
          </>
        );
      }
      const btn = (
        <ContactButton
          type="primary"
          icon={<MailOutlined />}
          onClick={() => {
            window.location.href = `mailto:${vendorEmail}?subject=Project Discussion - Clumse Referral`;
          }}
        >
          {emailLabel}
        </ContactButton>
      );
      return (
        <>
          {withNameTooltip(btn, vendorName, compactLabels)}
          {onMarkCompleted ? (
            <MarkSecondaryBtn type="default" onClick={onMarkCompleted}>
              Mark project complete
            </MarkSecondaryBtn>
          ) : null}
        </>
      );
    }

    const menuBtn = (
      <ContactButton
        type="primary"
        title={compactLabels ? vendorName : undefined}
      >
        <PhoneOutlined />
        {contactMenuLabel}
        <DownOutlined style={{ fontSize: 11 }} />
      </ContactButton>
    );

    return (
      <>
        <Dropdown
          menu={{ items: contactOptions }}
          trigger={["click"]}
          placement="topCenter"
        >
          {menuBtn}
        </Dropdown>
        {onMarkCompleted ? (
          <MarkSecondaryBtn type="default" onClick={onMarkCompleted}>
            Mark project complete
          </MarkSecondaryBtn>
        ) : null}
      </>
    );
  })();

  if (!inner) return null;

  if (inlineFooter) {
    return <ContactWrap>{inner}</ContactWrap>;
  }

  return <ContactStack>{inner}</ContactStack>;
}

export default ContactVendor;
